/**
 * Handles sprint routing logic and view rendering for:
 * - Sprint list
 * - Sprint edit/view page
 * - Calendar view (if version >= 2.0.0)
 * Also performs permission checks before rendering routes.
 *
 * @function SprintsMain
 * @returns {JSX.Element} Routed sprint-related pages or a permission message.
 */

import React from 'react'
import { observer } from 'mobx-react-lite'
import { Routes, Route } from 'react-router-dom'

import {
	actLoadSprint,
	requireSprintById
} from '../../../stores/sprints/actions'
import { useStores } from '../../../cls/stores/MobXProvider'
import { PageLoader } from '../../../components/Shared'
import SprintsLst from './SprintsLst'
import SprintEdit from './SprintEdit'
import SprintsCalendar from './SprintsCalendar'
import { requiredVersion } from '../../../config/versionUtils'
import { useSmartTranslation } from '../../../cls/TranslationContext'
import Page404 from '../../general/404Page'

const SprintsMain = () => {
	const { t } = useSmartTranslation()
	const { users, appType } = useStores()
	const { loggedInUser } = users
	const { appIsClient, appIsTeam } = appType
	const isAdmin = loggedInUser.isAdmin
	const isSuperAdmin = loggedInUser.isSuperAdmin

	const tableEntity = appIsClient
		? 'sprints'
		: appIsTeam
		? 'sprints_team'
		: ''

	/*
	 * If the user does not have permission to access the resource, then
	 * we display a message indicating that the user does not have permission to access the resource.
	 */
	if (!(isAdmin || isSuperAdmin || users.canUser('read', tableEntity))) {
		return (
			<div style={{ textAlign: 'center' }}>
				{t('__denied.access').charAt(0).toUpperCase() +
					t('__denied.access%__feature-off%_sprint')
						.toLowerCase()
						.slice(1)}
			</div>
		)
	}

	return (
		<Routes>
			<Route
				path=":itmId/*"
				element={
					<PageLoader
						forceLoad={true}
						requireItm={(id) => {
							return requireSprintById(id)
						}}
						loadItm={(id) => actLoadSprint(id)}
						targetLst="sprints"
					/>
				}
			>
				<Route path="edit" element={<SprintEdit />} />
				<Route path="view" element={<SprintEdit viewOnly={true} />} />
				<Route path="*" element={<Page404 />} />
			</Route>
			<Route index element={<SprintsLst />} />
			{requiredVersion('2.0.0') && (
				<Route path="calendar" element={<SprintsCalendar />} />
			)}
			<Route path="*" element={<Page404 />} />
		</Routes>
	)
}
export default observer(SprintsMain)
